/**
 * Utility functions for handling MOANI conversions without scientific notation
 */

/**
 * Convert MOANI to Wei (multiply by 1e18) without scientific notation
 * Uses BigInt like the game server for precision
 * @param {string|number} moaniAmount - Amount in MOANI
 * @returns {string} - Amount in Wei as string
 */
export const moaniToWei = (moaniAmount) => {
  if (!moaniAmount || moaniAmount === '0') return '0';
  
  const amount = parseFloat(moaniAmount);
  if (isNaN(amount)) return '0';
  
  // Use BigInt like the game server - multiply by 10^18
  const amountBigInt = BigInt(Math.round(amount * 1000)) * BigInt(10**15); // Avoid floating point precision issues
  return amountBigInt.toString();
};

/**
 * Convert Wei to MOANI (divide by 1e18) without scientific notation
 * @param {string|number} weiAmount - Amount in Wei
 * @returns {number} - Amount in MOANI as number
 */
export const weiToMoani = (weiAmount) => {
  if (!weiAmount || weiAmount === '0') return 0;
  
  try {
    // Handle erroneous scientific notation in database
    const weiString = weiAmount.toString();
    
    // Check if it's scientific notation (e.g., "5.863368460099885e+22")
    if (weiString.includes('e') || weiString.includes('E')) {
      console.warn('Found erroneous scientific notation in moani field:', weiString);
      
      // Just convert to number and treat as Wei directly
      const scientificValue = Number(weiString);
      if (isNaN(scientificValue)) return 0;
      
      // Convert directly to MOANI (divide by 1e18)
      const moaniValue = scientificValue / (10**18);
      console.log('Converted scientific notation to MOANI:', moaniValue);
      return moaniValue;
    }
    
    // Use BigInt for precise division like the game server
    const weiBigInt = BigInt(weiString);
    const moaniInteger = weiBigInt / BigInt(10**18);
    const remainder = weiBigInt % BigInt(10**18);
    
    // Convert to decimal
    const moaniDecimal = Number(moaniInteger) + Number(remainder) / (10**18);
    return moaniDecimal;
  } catch (error) {
    console.error('Error converting Wei to MOANI:', error, 'Input:', weiAmount);
    return 0; // Return 0 for invalid values
  }
};

/**
 * Format MOANI amount for display (legacy function)
 * @param {string|number} weiAmount - Amount in Wei
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} - Formatted MOANI amount
 * @deprecated Use MoaniDisplay component instead for consistent formatting with USD values
 */
export const formatMoani = (weiAmount, decimals = 2) => {
  const moaniAmount = weiToMoani(weiAmount);
  return moaniAmount.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

/**
 * Format MOANI amount with USD value
 * @param {string|number} weiAmount - Amount in Wei
 * @param {number} price - USD price per MOANI token
 * @param {number} decimals - Number of decimal places for MOANI (default: 2)
 * @param {number} usdDecimals - Number of decimal places for USD (default: 4)
 * @returns {string} - Formatted as "$M {amount} ({usd})"
 */
export const formatMoaniWithUSD = (weiAmount, price, decimals = 0, usdDecimals = 0) => {
  const moaniAmount = weiToMoani(weiAmount);
  const usdValue = moaniAmount * (price || 0.0025); // Fallback price
  
  const formattedMoani = moaniAmount.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });

  const formattedUSD = usdValue.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    style: 'currency',
    currency: 'USD'
  });

  return `$M ${formattedMoani} (${formattedUSD})`;
};

/**
 * Validate MOANI input
 * @param {string} input - User input
 * @returns {boolean} - Whether input is valid
 */
export const isValidMoaniAmount = (input) => {
  if (!input || input.trim() === '') return false;
  
  const amount = parseFloat(input);
  return !isNaN(amount) && amount >= 0 && amount <= 1000000000; // Max 1 billion MOANI
};


